import React, { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import { Button, Grid, Link, Typography } from '@mui/material';
import { FormInput, FormSelect, Loader, SelectOption } from '@omnigenbiodata/ui';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CreateStagedShipmentInput, ShipmentTypeEnum } from '../../../../../../../../core/api/lab.types';
import { aliquotLocationListThunk, responseSelector } from '../../../../../../../../store/aliquotLocationList';
import { useAppDispatch, useAppSelector } from '../../../../../../../../store';
import {
  getStagedShipmentMetaIsBusySelector,
  getStagedShipmentMetaThunk,
} from '../../../../../../../../store/stagedShipmentMetaList';
import ROUTES from '../../../../../../../../core/constants/routes.constants';
import { resetStagedShipment } from '../../../../../../../../store/stagedShipmentReset';
import { createStagedShipmentReset } from '../../../../../../../../store/stagedShipmentCreate';

interface CreateStagedShipmentProps {
  onSubmit: (values: CreateStagedShipmentInput) => void;
}

const shipmentTypeOptions: SelectOption[] = [
  { value: ShipmentTypeEnum.SEQUENCING, label: 'Sequencing' },
  { value: ShipmentTypeEnum.STORAGE, label: 'Storage' },
];

const validationSchema = Yup.object({
  destination: Yup.string().required('Destination is required'),
  shipmentType: Yup.string().required('Shipping type is required'),
  plannedShipmentDate: Yup.date().required('Planned shipment date is required'),
});

const CreateStagedShipment = ({ onSubmit }: CreateStagedShipmentProps) => {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const listAliquotLocations = useAppSelector(responseSelector);
  const stagedShipmentMetaIsBusy = useAppSelector(getStagedShipmentMetaIsBusySelector);

  const formik = useFormik<CreateStagedShipmentInput>({
    initialValues: {
      destination: '',
      shipmentType: '',
      plannedShipmentDate: '',
    },
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
  });

  useEffect(() => {
    dispatch(resetStagedShipment());
    dispatch(createStagedShipmentReset());
    dispatch(aliquotLocationListThunk(''));
    dispatch(getStagedShipmentMetaThunk());
  }, [dispatch]);

  const aliquotLocationOptions = useMemo<SelectOption[]>(
    () =>
      (listAliquotLocations ?? [])
        .filter(({ shippable }) => shippable)
        .map(({ aliquot_location_uuid, location }) => ({
          value: aliquot_location_uuid,
          label: location,
        })),
    [listAliquotLocations],
  );

  const goShippingHistory = () => {
    navigate(ROUTES.shippingHistory);
  };

  return (
    <>
      <Typography mb={2} variant="h3" align="center">
        {t('createShipmentStart.title')}
      </Typography>
      <Box
        component="form"
        onSubmit={formik.handleSubmit}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          p: 10,
          mb: 4,
          mt: 2,
          boxShadow: 1,
        }}
      >
        <Typography mt={-2} mb={2} variant="h5" align="center" fontWeight={600}>
          {t('createShipmentStart.text')}
        </Typography>

        <Grid container py={2} justifyContent="center" spacing={3}>
          <Grid item xs={12}>
            <Typography fontWeight={600} mb={1}>
              Shipment Destination
            </Typography>
            <FormSelect
              label=""
              name="destination"
              placeholder="Select Destination"
              margin="none"
              options={aliquotLocationOptions}
              value={formik.values.destination}
              error={formik.errors.destination}
              touched={formik.touched.destination}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography fontWeight={600} mb={1}>
              Shipment Type
            </Typography>
            <FormSelect
              label=""
              name="shipmentType"
              placeholder="Select Shipping Type"
              margin="none"
              options={shipmentTypeOptions}
              value={formik.values.shipmentType}
              error={formik.errors.shipmentType}
              touched={formik.touched.shipmentType}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography fontWeight={600} mb={-1}>
              Planned Shipment Date
            </Typography>
            <FormInput
              error={formik.errors.plannedShipmentDate}
              showErrorMessage={true}
              touched={formik.touched.plannedShipmentDate}
              name={`plannedShipmentDate`}
              label=""
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              type="date"
              value={formik.values.plannedShipmentDate}
              inputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
        </Grid>

        <Button type="submit" size="large" fullWidth variant="contained" disabled={!formik.isValid || !formik.dirty}>
          {t('createShipmentStart.createNewShipmentBtn')}
        </Button>
        <Box textAlign="center" mt={3}>
          <Link
            onClick={goShippingHistory}
            component="button"
            underline="always"
            sx={{
              fontWeight: 700,
              color: '#263445',
            }}
          >
            VIEW HISTORIC SHIPMENTS
          </Link>
        </Box>
      </Box>
      <Loader isVisible={stagedShipmentMetaIsBusy} label={t('loaders.loading') as string} />
    </>
  );
};

export default CreateStagedShipment;
